<script setup lang="ts">
import { ChevronDown, LoaderCircle, Wrench } from "lucide-vue-next";
import type {
  PublicWorkerServicesPageResponse,
  PublicWorkerServicesResponse,
} from "~/types/public-worker";
import { toTitleCase } from "~/utils/search-catalog";

const props = defineProps<{
  workerId: number;
  initialData: PublicWorkerServicesResponse;
}>();

const SERVICES_BATCH_SIZE = 6;
const { t } = useI18n();
const axios = useAxios();

const data = ref<PublicWorkerServicesResponse>(props.initialData);
const selectedCategory = ref("all");
const loading = ref(false);
const loadingMore = ref(false);
const loadError = ref("");
const categoryScrollerRef = ref<HTMLElement | null>(null);
const categoryButtonRefs = new Map<string, HTMLButtonElement>();

const categoryOptions = computed(() =>
  data.value.categories.map(category => ({
    ...category,
    label: catalogLabel("category", category.name),
  })),
);
const hasMultipleCategories = computed(() => categoryOptions.value.length > 1);
const totalServicesCount = computed(() =>
  data.value.categories.reduce((total, category) => total + category.count, 0),
);
const visibleServices = computed(() => data.value.items);
const canLoadMore = computed(
  () => data.value.hasMore && Boolean(data.value.nextCursor),
);

function catalogLabel(type: "category", value: string) {
  const key = `${type}.${value}`;
  const translated = t(key);

  return translated === key ? toTitleCase(value) : translated;
}

function setCategoryButtonRef(value: string, element: unknown) {
  if (element instanceof HTMLButtonElement) {
    categoryButtonRefs.set(value, element);
    return;
  }

  categoryButtonRefs.delete(value);
}

function revealCategory(value: string, behavior: ScrollBehavior = "smooth") {
  nextTick(() => {
    const scroller = categoryScrollerRef.value;
    const button = categoryButtonRefs.get(value);

    if (!scroller || !button || scroller.scrollWidth <= scroller.clientWidth) {
      return;
    }

    if (value === "all") {
      scroller.scrollTo({ left: 0, behavior });
      return;
    }

    const gutter = 16;
    const buttonLeft = button.offsetLeft;
    const buttonRight = buttonLeft + button.offsetWidth;
    const visibleLeft = scroller.scrollLeft + gutter;
    const visibleRight = scroller.scrollLeft + scroller.clientWidth - gutter;
    let left = scroller.scrollLeft;

    if (buttonLeft < visibleLeft) {
      left = buttonLeft - gutter;
    } else if (buttonRight > visibleRight) {
      left = buttonRight - scroller.clientWidth + gutter;
    } else {
      return;
    }

    scroller.scrollTo({
      left: Math.max(
        0,
        Math.min(left, scroller.scrollWidth - scroller.clientWidth),
      ),
      behavior,
    });
  });
}

async function fetchServices({
  append = false,
  cursor,
}: {
  append?: boolean;
  cursor?: string;
} = {}) {
  try {
    if (append) {
      loadingMore.value = true;
    } else {
      loading.value = true;
    }

    loadError.value = "";

    const response = await axios.get<PublicWorkerServicesPageResponse>(
      `/workers/${props.workerId}/services`,
      {
        params: {
          limit: SERVICES_BATCH_SIZE,
          cursor,
          categoryId:
            selectedCategory.value === "all"
              ? undefined
              : selectedCategory.value,
          includeMetadata: false,
        },
      },
    );

    const existingIds = append
      ? new Set(data.value.items.map(service => service.id))
      : null;
    const items = append
      ? [
          ...data.value.items,
          ...response.data.items.filter(
            service => !existingIds?.has(service.id),
          ),
        ]
      : response.data.items;

    data.value = {
      items,
      categories: data.value.categories,
      hasMore: response.data.hasMore,
      nextCursor: response.data.nextCursor,
    };

    return true;
  } catch (error) {
    console.error("Fetch services failed:", error);
    loadError.value = t("worker_profile.services_load_error");

    return false;
  } finally {
    loading.value = false;
    loadingMore.value = false;
  }
}

async function selectCategory(value: string) {
  if (loading.value || loadingMore.value || selectedCategory.value === value) {
    return;
  }

  const previousCategory = selectedCategory.value;
  selectedCategory.value = value;
  revealCategory(value);

  if (!(await fetchServices())) {
    selectedCategory.value = previousCategory;
    revealCategory(previousCategory);
  }
}

async function loadMoreServices() {
  if (!canLoadMore.value || loadingMore.value) {
    return;
  }

  await fetchServices({
    append: true,
    cursor: data.value.nextCursor || undefined,
  });
}

onMounted(() => {
  revealCategory(selectedCategory.value, "auto");
});
</script>

<template>
  <section
    id="portfolio"
    class="scroll-mt-36 border-b border-app-border py-10 sm:py-12"
  >
    <WorkerPublicSectionHeading
      :title="$t('worker_profile.services_and_portfolio')"
      :description="
        $t('worker_profile.services_count', {
          count: totalServicesCount,
        })
      "
    />

    <div
      v-if="hasMultipleCategories"
      ref="categoryScrollerRef"
      class="-mx-4 mt-5 flex scroll-px-4 gap-2 overflow-x-auto px-4 pb-1 overscroll-x-contain [scrollbar-width:none] sm:mx-0 sm:scroll-px-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
      :aria-label="$t('worker_profile.filter_services_by_category')"
    >
      <button
        :ref="element => setCategoryButtonRef('all', element)"
        type="button"
        class="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
        :class="
          selectedCategory === 'all'
            ? 'border-primary bg-primary text-white'
            : 'border-app-border bg-white text-app-text hover:border-primary/35 hover:bg-primary-50 hover:text-primary'
        "
        :aria-pressed="selectedCategory === 'all'"
        :disabled="loading || loadingMore"
        @click="selectCategory('all')"
      >
        {{ $t("worker_profile.all_categories") }}
        <span
          class="text-xs"
          :class="
            selectedCategory === 'all' ? 'text-white/80' : 'text-app-muted'
          "
        >
          {{ totalServicesCount }}
        </span>
      </button>

      <button
        v-for="category in categoryOptions"
        :key="category.id"
        :ref="element => setCategoryButtonRef(String(category.id), element)"
        type="button"
        class="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60"
        :class="
          selectedCategory === String(category.id)
            ? 'border-primary bg-primary text-white'
            : 'border-app-border bg-white text-app-text hover:border-primary/35 hover:bg-primary-50 hover:text-primary'
        "
        :aria-pressed="selectedCategory === String(category.id)"
        :disabled="loading || loadingMore"
        @click="selectCategory(String(category.id))"
      >
        {{ category.label }}
        <span
          class="text-xs"
          :class="
            selectedCategory === String(category.id)
              ? 'text-white/80'
              : 'text-app-muted'
          "
        >
          {{ category.count }}
        </span>
      </button>
    </div>

    <div class="mt-5 min-h-24">
      <div
        v-if="loading"
        class="flex min-h-40 items-center justify-center rounded-2xl border border-app-border bg-white"
      >
        <LoaderCircle class="size-6 animate-spin text-primary" />
      </div>

      <div v-else-if="visibleServices.length" class="grid gap-4 md:grid-cols-2">
        <WorkerPublicServiceCard
          v-for="service in visibleServices"
          :key="service.id"
          :service="service"
          :show-category="hasMultipleCategories"
        />
      </div>

      <div
        v-else
        class="rounded-2xl border border-dashed border-app-border bg-gray-50 px-5 py-12 text-center"
      >
        <Wrench class="mx-auto size-8 text-app-placeholder" />

        <p class="mt-3 text-sm font-semibold text-app-text">
          {{ $t("worker_profile.no_services_title") }}
        </p>

        <p class="mt-1 text-xs text-app-muted">
          {{ $t("worker_profile.no_services_description") }}
        </p>
      </div>
    </div>

    <p v-if="loadError" class="mt-4 text-center text-sm text-app-danger">
      {{ loadError }}
    </p>

    <div v-if="canLoadMore && !loading" class="mt-6 flex justify-center">
      <button
        type="button"
        class="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl border border-app-border bg-white px-5 py-2.5 text-sm font-semibold text-app-text transition hover:border-primary/35 hover:bg-primary-50 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-60"
        :disabled="loadingMore"
        @click="loadMoreServices"
      >
        <LoaderCircle v-if="loadingMore" class="size-4 animate-spin" />
        <ChevronDown v-else class="size-4" />

        {{ $t("worker_profile.show_more") }}
      </button>
    </div>
  </section>
</template>
